import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { PlanningService, PlanningFilterParcel, PlanningFilterExploitation } from '../../../services/planning.service';
import { Parcel } from '../../../models/parcel.model';
import { Farm } from '../../../models/farm.model';
import { ListItem, Specy, Variety } from '../../../models/lists.model';
import { ARBleService } from '../../../services/agrorendement-ble.service';
import { ARColzaService } from '../../../services/agrorendement-colza.service';
import { AROrgeService } from '../../../services/agrorendement-orge.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ModalService } from '../../../services/modal.service';
import { KeycloakService } from '../../../services/keycloak.service';
import { toNull, toNumber } from '../../../utils/utils';
import { ARBleCampaign } from '../../../models/agrorendement-ble.model';
import { Organisation } from '../../../models/organisation.model';
import { ListType, ListsService } from '../../../services/lists.service';
import { ARCommonListsService } from '../../../services/agrorendement-common-lists.service';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Params } from '@angular/router';
import { I18nTranslator } from '../../../i18n/translate';
import { ARColzaCampaign } from '../../../models/agrorendement-colza.model';
import { AROrgeCampaign } from '../../../models/agrorendement-orge.model';
import { CapitalizePipe } from '../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../../pipes/toLabel.pipe';
import { TranslateDatePipe, TranslatePipe } from '../../../pipes/translate.pipe';
import { adviceTypeMap, defaultWeekFields } from '../../../utils/mappings';
import { Router } from '@angular/router';
import { MONTHS } from '../../../utils/constants';
import {
  getWeekDates,
  getDateFromMonthDay,
  handleErroWithMsg,
  initializeDaysInMonth,
  getWeekDaysInMonthRange,
} from '../../../utils/planning-utils';
import { PlanningDataService } from '../../../services/planning-data.service';
import { SafePipe } from '../../../pipes/safe.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'planning-calendar',
  styleUrls: ['planning-calendar.component.scss'],
  templateUrl: './planning-calendar.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CapitalizePipe, ExtractAttributePipe, ToLabelPipe, TranslateDatePipe, HectareConverterPipe, TranslatePipe, SafePipe, CommonModule, FormsModule],
  providers: [
    PlanningService,
    ARColzaService,
    ARBleService,
    AROrgeService,
    ARCommonListsService,
    OrganisationService,
    PlanningDataService,
  ],
})
export class PlanningCalendar implements OnInit {
  private logTextFile: boolean = false;
  private logData: string = '';

  @Input() culture: string;
  @Input() passage: string;
  @Input() farms: Array<Farm>;
  @Input() parcels: Array<Parcel>;
  @Input('parcelIds') checkedParcelIds: Set<number>;
  @Input('parcelIdMapping') parcelIdsNames: Map<number, string>;

  @Output() modifier = new EventEmitter<Parcel>();
  @Output() modifierSelection = new EventEmitter();

  @ViewChild('checkboxAll') checkboxAll: any;

  // === Internal data tracking ===
  private weekDaySet: Set<string> = new Set<string>();
  private personalizedDateSet: Set<string> = new Set<string>();
  private weekStartDays: Set<string> = new Set<string>();
  private weekEndDays: Set<string> = new Set<string>();
  private weekAreaMap: Map<string, number> = new Map<string, number>();
  private personalizedAreaMap: Map<string, number> = new Map<string, number>();
  private weekSpans: Array<{
    weekNumber: number;
    days: Array<{ date: Date; day: number; month: string }>;
    middleMonth: string;
    middleDay: number;
    parcelIds: string[];
  }> = [];

  // === Filter fields ===
  public organisationFilter: number = null;
  public organisations: Organisation[] = [];
  public filterPostal: string = '';
  public prefixExploitation: string = '';
  public prefixAdvisor: string = '';
  public filterConseiller: string = '';
  static filterFarmToApply: PlanningFilterExploitation;
  static filterToApply: PlanningFilterParcel;

  // === Pagination & table ===
  currentPage: number = 0;
  pageSize: number = 10;
  totalPages: number = 1;
  paginatedParcels: any[] = [];

  // === Data referencing species/culture ===
  selectedSpecies: Specy = null;
  selectedCulture: string = null;
  public farmIds: number[] = [];
  adviceType: string = null;
  lengthAllParcels: number;
  editingParcelId: number = null;
  date: Date = null;
  dateStr: string = null;
  dateChanged: boolean = false;
  hasUnsavedChanges: boolean = false;

  // === Additional filter fields ===
  public prefixParcel: string = '';
  public prefixFilterIdParcel: number = null;
  public dateAvant: string = null;
  public dateApres: string = null;
  public datePlanningAfter: string = null;
  public datePlanningBefore: string = null;
  public specyFilter: number | string = null;
  public varietyFilter: number | string = null;
  public personalizedDateFilter: number | string = null;
  public campaignFilter: number;
  public campaignYear: number;
  public suspendedFilter: boolean = false;

  // === UI & data handling flags ===
  public showModifyPopup: boolean = false;
  colzaCulture: boolean = false;
  public isAdmin: boolean;
  campaignsOrge: AROrgeCampaign[];
  campaignsBle: ARBleCampaign[];
  campaignsColza: ARColzaCampaign[];

  public parsedPersonalizedData: any[] = [];
  public parsedWeeksData: any[] = [];
  cellWidth: number = 40;
  weeks: number[] = Array.from({ length: 52 }, (_, i) => i + 1);
  days: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  months: string[] = MONTHS;
  private daysInMonth: number[] = [31, 28, 31, 30, 31, 30, 31, 31];

  predeterminedData: any = {};
  personalizedData: any = {};
  parcelData: any = {};
  dragging: boolean = false;
  startCell: any = null;
  currentDragCells: string[] = [];
  unpainting: boolean = false;

  farmParcelIndex = new Map<number, Array<Parcel>>();
  showSpeciesFilter: Specy[] = [];
  showVarietyFilter: Variety[] = [];
  public fullParcel: Observable<any>;
  public lastGet: Set<number> = new Set<number>();

  public filter: string = '';
  speciesDict: Map<number, Specy>;
  species: Specy[];

  public isBreadcrumbAlreadyRendered: boolean = false;

  dateToParcelIdsMap: Map<string, string[]> = new Map<string, string[]>();
  parcelCountMap: Map<string, number> = new Map<string, number>();
  campaign: any;
  currentYear: number = new Date().getFullYear();

  constructor(
    private planningService: PlanningService,
    private organisationService: OrganisationService,
    private modal: ModalService,
    private listsService: ListsService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private planningDataService: PlanningDataService,
    private route: ActivatedRoute,
  ) {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  ngOnInit() {
    this.isBreadcrumbAlreadyRendered = !!document.querySelector('.breadcrumb');

    const qParams = this.getQueryParamsFromLocation();

    const allCampaigns$ = this.planningDataService.getAllCampaigns();
    const organisations$ = this.getOrganisations();
    const species$ = this.getSpecies();

    if (qParams['reload']) {
      this.culture = qParams['culture'];
      this.passage = qParams['passage'];
      this.campaign = qParams['campaign'];
    }

    this.selectedCulture = this.culture ? this.culture : 'ble';

    this.adviceType = this.getAdviceTypeFromPassage(this.passage);

    forkJoin([allCampaigns$, organisations$, species$]).subscribe(
      (data: Array<any>) => {
        const bleCampaigns: ARBleCampaign[] = data[0][0];
        const orgeCampaigns: AROrgeCampaign[] = data[0][1];
        const colzaCampaigns: ARColzaCampaign[] = data[0][2];

        this.organisations = data[1];
        const lists = data[2];
        const species = lists.get(ListType.SPECIES);

        this.species = species.sort((a, b) => a.label.localeCompare(b.label));
        this.speciesDict = new Map<number, Specy>();
        for (const sp of species) {
          this.speciesDict.set(sp.id, sp);
        }

        this.initializeCampaignFilters([bleCampaigns, orgeCampaigns, colzaCampaigns]);

        this.daysInMonth = initializeDaysInMonth(this.campaignYear);

        this.currentYear = this.campaignYear;

        this.setSelectedSpecies();
      },
      (err) => {
        console.error('Error in fetching campaign or filter data:', err);
      },
    );
    console.log('ngOnInit: culture:', this.selectedCulture, 'passage:', this.passage);
  }

  // ------------------------------------------------------------------
  //  Lifecycle & Initialization
  // ------------------------------------------------------------------
  setSelectedSpecies() {
    if (this.species && this.specyFilter) {
      this.selectedSpecies = this.species.find((sp) => sp.id === this.specyFilter) || null;
    } else {
      this.selectedSpecies = null;
    }
  }

  // ------------------------------------------------------------------
  //  Filters
  // ------------------------------------------------------------------
  applyFilters() {
    this.fetchAndProcessParcels();
  }

  resetFilters() {
    this.prefixExploitation = '';
    this.prefixAdvisor = '';
    this.organisationFilter = null;

    this.initializeParcelsAndCalendar();
  }

  // ------------------------------------------------------------------
  //  Data & Campaign
  // ------------------------------------------------------------------
  initializeCampaignFilters(data: Array<any>) {
    let temp: any;
    if (this.culture === 'ble') {
      temp = data[0];
    } else if (this.culture === 'orge') {
      temp = data[1];
    } else if (this.culture === 'colza') {
      temp = data[2];
    }

    if (temp && temp.length > 0 && temp[0] && temp[0].id !== undefined) {
      this.campaignFilter = temp[0].id;
      const endingOnStr: string = temp[0].endingOn;
      const endingOnDate: Date = new Date(endingOnStr);
      this.campaignYear = endingOnDate.getUTCFullYear();
    } else {
      this.campaignFilter = null;
    }

    this.updateUIWithFilters();
    this.handleListFarm('name', 'asc', PlanningCalendar.filterFarmToApply);
  }

  updateUIWithFilters() {
    if (PlanningCalendar.filterToApply) {
      this.filterPostal = PlanningCalendar.filterToApply.postalCode;
      this.prefixExploitation = PlanningCalendar.filterToApply.prefixExploitation;
      this.prefixAdvisor = PlanningCalendar.filterToApply.prefixAdvisor;
      this.prefixParcel = PlanningCalendar.filterToApply.prefixParcel;
      this.prefixFilterIdParcel = PlanningCalendar.filterToApply.prefixIdParcel;
      this.dateAvant = PlanningCalendar.filterToApply.dateAvant;
      this.dateApres = PlanningCalendar.filterToApply.dateApres;

      if (this.colzaCulture) {
        this.varietyFilter = PlanningCalendar.filterToApply.variety;
      } else {
        this.specyFilter = PlanningCalendar.filterToApply.specy;
      }

      this.organisationFilter = toNumber(PlanningCalendar.filterToApply.organisationId);
      this.personalizedDateFilter = PlanningCalendar.filterToApply.personalizedDate;
    }
  }

  // ------------------------------------------------------------------
  //  Service calls
  // ------------------------------------------------------------------

  getOrganisations(): Observable<Organisation[]> {
    const obs = this.organisationService.getOrganisations().pipe(share());
    obs.subscribe({
      next: (orgs) => {
        const orga = orgs.sort((a, b) => a.name.localeCompare(b.name));
        this.organisations = orga;
      },
    });
    return obs;
  }

  getSpecies(): Observable<Map<ListType, ListItem[]>> {
    const obs = this.listsService.getListTypes(ListType, ListType.SPECIES).pipe(share());
    obs.subscribe(
      (lists: Map<ListType, ListItem[]>) => {
        const species = lists.get(ListType.SPECIES);
        this.species = species.sort((a, b) => a.label.localeCompare(b.label));
        this.speciesDict = new Map<number, Specy>();
        for (const sp of species) {
          this.speciesDict.set(sp.id, sp);
        }
      },
      (err) =>
        handleErroWithMsg(
          err,
          this.i18nTranslator.translate('planning.error.campagne'),
          this.modal,
          this.i18nTranslator,
        ),
    );
    return obs;
  }

  // ------------------------------------------------------------------
  //  Core Data Loading & Processing
  // ------------------------------------------------------------------
  initializeParcelsAndCalendar() {
    if (!this.farmIds || this.farmIds.length === 0) {
      console.error('No farm IDs available.');
      return;
    }
    const adviceType = this.getAdviceTypeFromPassage(this.passage);
    this.fetchAndProcessParcels();
  }

  processCalendarData(data: any) {
    this.clearCalendarSets();

    const customizedDateData = data.customized_date;
    const defaultWeekData = data.default_week;

    const parcelsWithPersonalizedDates = this.processCustomizedDateData(customizedDateData);

    // Process default weeks only for parcels not in personalized dates
    this.processDefaultWeekData(defaultWeekData, parcelsWithPersonalizedDates);
    this.logParcelMapSummary();
  }

  private clearCalendarSets(): void {
    this.weekDaySet.clear();
    this.personalizedDateSet.clear();
    this.weekStartDays.clear();
    this.weekEndDays.clear();
    this.weekAreaMap.clear();
    this.personalizedAreaMap.clear();
    this.dateToParcelIdsMap.clear();
    this.parcelCountMap.clear();
    this.weekSpans = [];
  }

  private processCustomizedDateData(customizedDateData: any[]): Set<string> {
    const parcelsWithPersonalizedDates = new Set<string>();

    if (customizedDateData && customizedDateData.length > 0) {
      customizedDateData.forEach((item) => {
        const dateStr = item.date_livraison;
        const parcelIdsStr = item.parcel_ids;
        const dateObj = new Date(dateStr);

        if (!isNaN(dateObj.getTime())) {
          const dateYear = dateObj.getUTCFullYear();
          if (dateYear !== this.campaignYear) {
            console.log(
              `Filtering out personalized date ${dateStr} (year ${dateYear}) - doesn't match campaign year ${this.campaignYear}`,
            );
            return;
          }

          const monthIndex = dateObj.getUTCMonth();
          const day = dateObj.getUTCDate();
          const month = this.months[monthIndex];
          const key = `${month}-${day}-customized`;

          this.personalizedDateSet.add(key);

          const parcelIdsArray = parcelIdsStr.split(',').map((id) => id.trim());
          parcelIdsArray.forEach((parcelId) => parcelsWithPersonalizedDates.add(parcelId));

          this.dateToParcelIdsMap.set(key, parcelIdsArray);
          this.parcelCountMap.set(key, parcelIdsArray.length);

          console.log(
            `Personalized Date: Total parcel count for date ${dateStr} (month ${month}, day ${day}): Parcel IDs: ${parcelIdsArray.join(', ')}`,
          );
        } else {
          console.warn(`Invalid date in customized_date: ${dateStr}`);
        }
      });
    }

    return parcelsWithPersonalizedDates;
  }

  private processDefaultWeekData(defaultWeekData: any[], parcelsWithPersonalizedDates: Set<string>): void {
    if (!defaultWeekData || defaultWeekData.length === 0) {
      return;
    }
    defaultWeekData.forEach((item) => {
      const weekNumber = item.week;
      const parcelIdsStr = item.parcel_ids;
      const allParcelIdsArray = parcelIdsStr.split(',').map((id) => id.trim());

      const initialCount = allParcelIdsArray.length;
      // Filter out parcels that already have a customized date
      const parcelIdsArray = allParcelIdsArray.filter((parcelId) => !parcelsWithPersonalizedDates.has(parcelId));
      const finalCount = parcelIdsArray.length;

      if (initialCount !== finalCount) {
        const excludedParcels = allParcelIdsArray.filter((id) => parcelsWithPersonalizedDates.has(id));
        this.logData += `\n==============================================\n`;
        this.logData += `DW ${weekNumber}:\n`;
        this.logData += `Subtotal = ${initialCount}\n`;
        this.logData += `Total    = ${finalCount}\n`;
        this.logData += `Parcels  (${initialCount} total):\n  ${allParcelIdsArray.join(', ')}\n\n`;

        if (excludedParcels.length > 0) {
          this.logData += `PD parcels (excluded from DW):\n  ${excludedParcels.join(', ')}\n`;
        } else {
          this.logData += `PD parcels (excluded from DW): None\n`;
        }

        this.logData += `==============================================\n`;
      } else {
        this.logData += `\n==============================================\n`;
        this.logData += `DW ${weekNumber} - NO PD\n`;
        this.logData += `Total = ${finalCount}\n`;
        this.logData += `Parcels (${finalCount} total): ${allParcelIdsArray.join(', ')}\n`;
        this.logData += `==============================================\n`;
      }

      if (parcelIdsArray.length === 0) {
        return;
      }

      const weekDates = getWeekDates(weekNumber, this.months, this.campaignYear);
      const middleIndex = Math.floor(weekDates.length / 2);
      const middleDateInfo = weekDates[middleIndex];
      const middleMonth = middleDateInfo.month;
      const middleDay = middleDateInfo.day;

      this.weekSpans.push({
        weekNumber: weekNumber,
        days: weekDates,
        middleMonth: middleMonth,
        middleDay: middleDay,
        parcelIds: parcelIdsArray,
      });

      // Mark all days in the week as part of the weekDaySet
      weekDates.forEach((dateInfo) => {
        const key = `${dateInfo.month}-${dateInfo.day}`;
        this.weekDaySet.add(key);
      });

      // Attach the parcel IDs to the middle cell of the week
      const middleKey = `${middleMonth}-${middleDay}`;
      const existingParcelIds = this.dateToParcelIdsMap.get(middleKey) || [];
      const newParcelIds = parcelIdsArray.filter((id) => existingParcelIds.indexOf(id) === -1);

      if (newParcelIds.length > 0) {
        this.dateToParcelIdsMap.set(middleKey, existingParcelIds.concat(newParcelIds));
        this.parcelCountMap.set(middleKey, this.dateToParcelIdsMap.get(middleKey).length);
      }

      // Mark start & end
      const startDayInfo = weekDates[0];
      const endDayInfo = weekDates[weekDates.length - 1];
      const startKey = `${startDayInfo.month}-${startDayInfo.day}`;
      const endKey = `${endDayInfo.month}-${endDayInfo.day}`;
      this.weekStartDays.add(startKey);
      this.weekEndDays.add(endKey);
    });
  }

  private logParcelMapSummary(): void {
    console.log('--- Summary of Parcels per Date ---');
    this.dateToParcelIdsMap.forEach((parcelIdsArray, dateKey) => {
      const parcelCount = parcelIdsArray.length;
      const dateType = this.personalizedDateSet.has(dateKey) ? 'Personalized Date' : 'Default Week';
    });
  }

  handleListFarm(sortColumnName: string, sortColumnOrder: string, filter: PlanningFilterExploitation) {
    this.planningService
      .getFarms(this.culture, this.passage, this.campaignFilter, null, sortColumnName, sortColumnOrder, filter, null)
      .subscribe(
        (result: any) => {
          this.farmIds = result;
          this.fetchAndProcessParcels();
        },
        (e) => {
          console.log(e);
        },
      );
  }

  private fetchAndProcessParcels(): void {
    this.planningService
      .getParcelsGroupedByDateAndWeek(
        this.culture,
        this.farmIds,
        this.adviceType,
        this.campaignFilter,
        this.prefixExploitation,
        this.organisationFilter,
        this.prefixAdvisor,
      )
      .subscribe(
        (data) => {
          if (!data || !data.data) {
            console.error('No data returned from API.');
            return;
          }
          this.processCalendarData(data.data);
        },
        (err) => {
          console.error('Error fetching planning data:', err);
        },
      );
  }

  processParcels(response: any) {
    this.farms = [];
    let farmId = 0;
    const fullParc: any[] = [];
    const list = response.data.content;
    this.lengthAllParcels = response.data.totalElements;
    this.totalPages = response.data.totalPages;
    this.paginatedParcels = list;
    const parcelObservables: Observable<any>[] = [];

    list.forEach((element: any) => {
      const parcel$ = this.planningService.getParcel(element.id, this.culture).pipe(
        tap((parcel: any) => {
          farmId = parcel.farm.id;
          fullParc.push(parcel);
        }),
      );
      parcelObservables.push(parcel$);
    });

    fullParc.sort((a, b) => a.id - b.id);

    forkJoin(parcelObservables).subscribe(
      () => {
        console.log('All parcels fetched. Setting fullParcel.');
        this.fullParcel = of(fullParc);
        this.initializeParcelsAndCalendar();
      },
      (error) => {
        console.log('Error fetching parcels:', error);
      },
    );
  }

  // ------------------------------------------------------------------
  //  UI / Helper Methods
  // ------------------------------------------------------------------
  getAdviceTypeFromPassage(passage: string): string {
    return adviceTypeMap[passage] || passage;
  }

  getPassageDisplayLabel(passage: string): string {
    const passageDisplayMap: { [key: string]: string } = {
      BLET1: 'Tallage',
      BLET2: 'Début montaison',
      BLET3: '3 Nœuds ou DFE',
      BLEDN: '2 Nœuds',
      ORGET1: 'Tallage',
      ORGET2: 'Début montaison',
      ORGET3: 'Fin montaison',
      COLZASH: 'Sortie Hiver',
      COLZAEH: 'Entrée Hiver',
    };
    return passageDisplayMap[passage] || passage;
  }

  resetFilterToApply() {
    PlanningCalendar.filterToApply = {
      prefixParcel: this.prefixParcel,
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      dateAvant: this.dateAvant,
      dateApres: this.dateApres,
      postalCode: this.filterPostal,
      prefixExploitation: this.prefixExploitation,
      prefixAdvisor: this.prefixAdvisor,
      datePlanningAfter: this.datePlanningAfter,
      datePlanningBefore: this.datePlanningBefore,
      specy: this.specyFilter,
      variety: this.varietyFilter,
      organisationId: toNumber(this.organisationFilter),
      personalizedDate: this.personalizedDateFilter,
      suspended: false,
    };
  }

  // ------------------------------------------------------------------
  //  Calendar Cell Conditionals
  // ------------------------------------------------------------------
  isDayInPersonalizedDate(month: string, day: number): boolean {
    const key = `${month}-${day}-customized`;
    return this.personalizedDateSet.has(key);
  }

  isStartOfWeek(month: string, day: number): boolean {
    const key = `${month}-${day}`;
    return this.weekStartDays.has(key);
  }

  isEndOfWeek(month: string, day: number): boolean {
    const key = `${month}-${day}`;
    return this.weekEndDays.has(key);
  }

  isDayBeyondMonthEnd(monthIndex: number, day: number): boolean {
    return day > this.daysInMonth[monthIndex];
  }

  isStartOfPersonalizedSpan(month: string, day: number): boolean {
    return this.isDayInPersonalizedDate(month, day);
  }

  isEndOfPersonalizedSpan(month: string, day: number): boolean {
    return this.isDayInPersonalizedDate(month, day);
  }

  isDayInParcelWeekRange(month: string, day: number): boolean {
    const key = `${month}-${day}`;
    return this.weekDaySet.has(key);
  }

  // ------------------------------------------------------------------
  //  Calendar Cell: Parcel Count
  // ------------------------------------------------------------------
  shouldDisplayParcelCount(month: string, day: number, type: 'default' | 'personalized'): boolean {
    const key = `${month}-${day}`;
    if (type === 'default') {
      const weekSpan = this.weekSpans.find((span) => span.middleMonth === month && span.middleDay === day);
      return !!weekSpan;
    } else if (type === 'personalized') {
      return this.personalizedDateSet.has(key + '-customized');
    }
    return false;
  }

  getParcelCount(month: string, day: number, type: 'default' | 'personalized'): number {
    const key = `${month}-${day}`;
    if (type === 'default') {
      const weekSpan = this.weekSpans.find((span) => span.middleMonth === month && span.middleDay === day);
      if (weekSpan) {
        return this.parcelCountMap.get(key) || 0;
      }
    } else if (type === 'personalized') {
      return this.personalizedDateSet.has(key + '-customized') ? this.parcelCountMap.get(key + '-customized') || 0 : 0;
    }
    return 0;
  }

  // ------------------------------------------------------------------
  //  Navigation & Click Events
  // ------------------------------------------------------------------
  onClick(type: string, month: string, day: number) {
    const queryParams: any = {
      culture: this.culture,
      passage: this.passage,
      prefixExploitation: this.prefixExploitation,
      organisationId: this.organisationFilter,
      prefixAdvisor: this.prefixAdvisor,
    };

    if (type === 'default') {
      const weekSpan = this.weekSpans.find((span) =>
        span.days.some((dateInfo) => dateInfo.month === month && dateInfo.day === day),
      );

      if (weekSpan) {
        const startDate = weekSpan.days[0].date; // earliest day in that week
        const endDate = weekSpan.days[weekSpan.days.length - 1].date; // latest day in that week

        queryParams.weekNumber = weekSpan.weekNumber;
        queryParams.dateApres = this.formatDateForQuery(startDate);
        queryParams.dateAvant = this.formatDateForQuery(endDate);
        queryParams.personalizedDate = false; // Indicate this is a personalized date
      }
    } else if (type === 'personalized') {
      const date = getDateFromMonthDay(this.months, month, day, this.campaignYear);
      if (date) {
        queryParams.date = this.formatDateForQuery(date);
        queryParams.personalizedDate = true; // Indicate this is a personalized date
      }
    }

    // Navegar con los `queryParams` construidos dinámicamente
    this.router.navigate(['/planning-parcel'], { queryParams });
  }

  private formatDateForQuery(date: Date): string {
    if (!date || isNaN(date.getTime())) return '';
    const corrected = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return corrected.toISOString().split('T')[0]; // 'YYYY-MM-DD'
  }

  onViewAllParcelsClick() {
    this.router.navigate(['/planning-parcel'], {
      queryParams: {
        culture: this.culture,
        passage: this.passage,
      },
    });
  }

  onPredeterminedDoubleClick(month: string, week: number) {
    console.log('Double-clicked on predetermined date:', month, week);
  }

  onDateCellClick(type: string, month: string, day: number): void {
    console.log(`Clicked on ${type} date: ${month} ${day}`);
  }

  calculatePersonalizedArea(month: string, day: number): number {
    const key = `${month}-${day}`;
    const totalArea = this.personalizedAreaMap.get(key) || 0;
    return Math.round(totalArea);
  }

  getWeekDaysInMonth(weekNumber: number) {
    return getWeekDaysInMonthRange(weekNumber, this.campaignYear, this.months);
  }

  getDefaultWeek(parcel: any): number {
    if (parcel.orderItem) {
      const cultureFields = defaultWeekFields[this.culture];
      if (cultureFields) {
        const field = cultureFields[this.passage];
        if (field && parcel.orderItem[field] && parcel.orderItem[field].week) {
          return parcel.orderItem[field].week;
        }
      }
    }
    return 0;
  }

  private getQueryParamsFromLocation(): any {
    const search = location.search;
    const params: any = {};

    if (!search || !search.startsWith('?')) {
      return params;
    }

    // remove leading '?'
    const queryString = search.substring(1);
    const parts = queryString.split('&');
    for (const part of parts) {
      const [k, v] = part.split('=');
      // decode e.g. "?culture=ble&passage=BLET1"
      params[decodeURIComponent(k)] = decodeURIComponent(v || '');
    }
    return params;
  }
}
